"use client"

import { useState } from "react"
import { observer } from "mobx-react-lite"
import { useAppContext } from "../../../../shared/functions/Context"
import { hideModalFromId } from "../../../../shared/functions/ModalShow"
import MODAL_NAMES from "../../../dialogs/ModalName"
import { Trash2 } from "lucide-react"

interface IDeleteConfirmationModalProps {
  itemType?: "template" | "value" | "leadership"
  itemName?: string
}

const DeleteConfirmationModal = observer(({ itemType = "template", itemName }: IDeleteConfirmationModalProps) => {
  const { api, store } = useAppContext()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleConfirmDelete = async () => {
    setLoading(true)
    setError(null)

    try {
      if (itemType === "template" && store.templates.selected) {
        await api.templates.delete(store.templates.selected)
        await api.templates.getAll()
      } else if (itemType === "value" && store.value.selected) {
        await api.value.delete(store.value.selected)
        await api.value.getAll()
      } else if (itemType === "leadership" && store.leadership.selected) {
        await api.leadership.delete(store.leadership.selected)
        await api.leadership.getAll()
      }

      setLoading(false)
      onCancel()
    } catch (err) {
      setLoading(false)
      setError(err instanceof Error ? err.message : "Failed to delete item")
      console.error("Error deleting item:", err)
    }
  }

  const onCancel = () => {
    setError(null)
    const modalName =
      itemType === "template"
        ? MODAL_NAMES.THREE_SIXTY_FEEDBACK.DELETE_TEMPLATE_MODAL
        : itemType === "value"
          ? MODAL_NAMES.THREE_SIXTY_FEEDBACK.DELETE_VALUE_MODAL
          : MODAL_NAMES.THREE_SIXTY_FEEDBACK.DELETE_LEADERSHIP_MODAL

    hideModalFromId(modalName)
  }

  return (
    <div className="user-modal uk-modal-dialog uk-modal-body uk-margin-auto-vertical" data-uk-overflow-auto>
      <button className="uk-modal-close-default">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          style={{ fill: "rgba(0, 0, 0, 1)" }}
        >
          <path d="M9.172 16.242 12 13.414l2.828 2.828 1.414-1.414L13.414 12l2.828-2.828-1.414-1.414L12 10.586 9.172 7.758 7.758 9.172 10.586 12l-2.828 2.828z"></path>
          <path d="M12 22c5.514 0 10-4.486 10-10S17.514 2 12 2 2 6.486 2 12s4.486 10 10 10zm0-18c4.411 0 8 3.589 8 8s-3.589 8-8 8-8-3.589-8-8 3.589-8 8-8z"></path>
        </svg>
      </button>

      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
          <Trash2 className="h-5 w-5 text-red-600" />
        </div>
        <h3 className="uk-modal-title text-red-600">Delete {itemType}</h3>
      </div>

      <div className="dialog-content uk-position-relative">
        {error && (
          <div className="uk-alert-danger" data-uk-alert>
            <a className="uk-alert-close" data-uk-close></a>
            <p>{error}</p>
          </div>
        )}

        <div className="uk-margin">
          <p className="text-slate-700 mb-2">Are you sure you want to delete this {itemType}?</p>
          {itemName && (
            <p className="text-slate-600 font-semibold bg-slate-50 p-3 rounded-lg border border-slate-200">
              <span className="text-slate-500">Item: </span>
              {itemName}
            </p>
          )}
          <p className="text-slate-500 text-sm mt-3">This action cannot be undone. Please proceed with caution.</p>
        </div>

        <div className="uk-width-1-1 uk-text-right uk-margin-top">
          <button className="btn-text uk-margin-right" type="button" onClick={onCancel} disabled={loading}>
            Cancel
          </button>
          <button className="btn btn-danger" type="button" onClick={handleConfirmDelete} disabled={loading}>
            Delete {loading && <div data-uk-spinner="ratio: .5"></div>}
          </button>
        </div>
      </div>
    </div>
  )
})

export default DeleteConfirmationModal
